import { gsap } from 'gsap';
import MorphSVGPlugin from 'gsap/MorphSVGPlugin';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { Icon, Toggle } from '@column/column-ui-kit';
import chroma from 'chroma-js';
import { MenuItem } from './MenuItem';
import { ROUTE } from '~/app/routes';
import { Logo } from '~/elements';
import { useSessionStore } from '~/stores/Session';
import { log } from '~/util';

gsap.registerPlugin(MorphSVGPlugin);

interface SidebarProps {
  className?: string;
}

export const PROFILE_ENTRIES = [
  {
    label: 'Profile',
    path: ROUTE.PROFILE,
  },
  {
    label: 'Your Platforms',
    path: ROUTE.PLATFORMS,
    activePath: `${ROUTE.PLATFORMS}/edit`,
  },
];

export const PLATFORM_ENTRIES = [
  {
    label: 'Info',
    path: ROUTE.PLATFORM,
    activePath: ROUTE.PLATFORM,
  },
  {
    label: 'Settings',
    path: ROUTE.PLATFORM_SETTINGS,
    activePath: ROUTE.PLATFORM_SETTINGS,
  },
  {
    label: 'API Keys',
    path: ROUTE.PLATFORM_API_KEYS,
    activePath: `${ROUTE.PLATFORM_API_KEYS}/edit`,
  },
  {
    label: 'Webhooks',
    path: ROUTE.PLATFORM_WEBHOOKS,
    activePath: `${ROUTE.PLATFORM_WEBHOOKS}/edit`,
  },
  {
    label: 'Root Entity (Sandbox)',
    path: ROUTE.PLATFORM_ROOT_ENTITY,
    isSandboxOnly: true,
  },
];

export const LOANS_ENTRIES = [
  {
    label: 'Overview',
    path: ROUTE.LOANS,
    activePath: `${ROUTE.LOANS}/edit`,
  },
  {
    label: 'Disbursements',
    path: `${ROUTE.LOANS}/disbursements`,
    activePath: `${ROUTE.LOANS}/disbursements`,
  },
  {
    label: 'Payments',
    path: `${ROUTE.LOANS}/payments`,
    activePath: `${ROUTE.LOANS}/payments`,
  },
];

export const EVENTS = [
  {
    label: 'Overview',
    path: ROUTE.EVENTS,
  },
  {
    label: 'Webhook',
    path: ROUTE.EVENTS_WEBHOOK,
  },
];

const Wrapper = styled.div`
  --sidebar-o: 0;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 240px;
  display: flex;
  gap: 36px;
  flex-direction: column;
  box-sizing: border-box;
  background-color: ${({ theme }) =>
    chroma
      .mix(theme.background, theme.secondary.background, theme.id !== 'default' ? 0.0025 : 0.075)
      .saturate(0.05)
      .hex()};
  transition: padding-top 0.15s;
`;

const StyledLogo = styled(Logo)`
  margin-bottom: 12px;
`;

const Blur = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  overflow: hidden;
  opacity: var(--sidebar-o);
  will-change: opacity;

  &:before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.primary.background};
    filter: blur(100px);
    opacity: 0.02;
    border-radius: 100%;
    transform: translate(50%, -50%);
  }
`;

const Nav = styled.div<{ $showSandboxBanner?: boolean }>`
  --sidebar-scrollbar-background: transparent;
  --sidebar-thumb-background: transparent;

  display: flex;
  flex-direction: column;
  grid-gap: 24px;
  opacity: var(--sidebar-o);
  height: 100%;
  margin: 0;
  padding: 32px 24px 140px 24px;
  padding-top: ${({ $showSandboxBanner }) => $showSandboxBanner && '72px'};
  transition: padding-top 0.15s;

  scrollbar-width: thin;
  scrollbar-color: var(--sidebar-thumb-background) var(--sidebar-scrollbar-background);
  overflow-y: scroll;

  &:hover {
    --sidebar-thumb-background: ${({ theme }) => theme.secondary.blendToBackground(150)};
    color: #666666ff;
  }

  @media (min-height: 820px) {
    scrollbar-width: none;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: var(--sidebar-scrollbar-background);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--sidebar-thumb-background);
    border-radius: 6px;
    border: 3px solid var(--sidebar-scrollbar-background);
  }
`;

const Bottom = styled.div`
  margin-top: auto;
  display: grid;
  grid-gap: 16px;
  z-index: 1;
  position: fixed;
  padding: 20px 12px 28px;
  bottom: 0;
  left: 16px;
  width: 196px;
  background-color: ${({ theme }) =>
    chroma
      .mix(theme.background, theme.secondary.background, theme.id !== 'default' ? 0.0025 : 0.075)
      .saturate(0.05)
      .hex()};
  transition: border-color 0.15s;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  @media (min-height: 820px) {
    border-color: transparent;
  }
`;

const Sandbox = styled.div`
  color: ${({ theme }) => theme.secondary.background};
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-top: auto;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(1000, 700)};
  }
`;

const StyledToggle = styled(Toggle)`
  margin-left: auto;
`;

const Section = styled.div`
  display: grid;
  grid-gap: 4px;
`;

const Label = styled.div`
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
  text-transform: uppercase;
`;

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>((props, ref) => {
  const { showSandboxBanner, isSandbox, setSandbox, currentUser, currentPlatform } = useSessionStore();

  const handleSandboxChange = (value: boolean) => {
    setSandbox(value);

    log({
      name: value ? 'Sandbox on' : 'Production on',
      context: currentUser,
    });
  };

  return (
    <Wrapper className={props.className} ref={ref}>
      <Blur />
      <Nav $showSandboxBanner={showSandboxBanner}>
        <StyledLogo variant="word" size="16px" />
        <Section>
          <Label>Main</Label>
          <MenuItem label="Home" icon={<Icon.AnimationHome />} path={ROUTE.ROOT} exact />
          <MenuItem
            label="Platform"
            icon={<Icon.AnimationLayers />}
            path={ROUTE.PLATFORM}
            entries={PLATFORM_ENTRIES}
            activePath={ROUTE.PLATFORM_ROOT}
          />
          <MenuItem label="User" icon={<Icon.AnimationUser />} path={ROUTE.PROFILE} entries={PROFILE_ENTRIES} />
        </Section>
        <Section>
          <Label>Data</Label>
          <MenuItem label="Transfers" icon={<Icon.AnimationArrows />} path={ROUTE.TRANSFERS} />
          <MenuItem label="Loans" icon={<Icon.AnimationHandCoins />} path={ROUTE.LOANS} entries={LOANS_ENTRIES} />
          <MenuItem label="Accounts" icon={<Icon.AnimationWallet />} path={ROUTE.BANK_ACCOUNTS} />
          <MenuItem label="Entities" icon={<Icon.AnimationDocument />} path={ROUTE.ENTITIES} />
        </Section>
        <Section>
          <Label>Monitoring</Label>
          <MenuItem label="Reporting" icon={<Icon.AnimationDocumentEdit />} path={ROUTE.REPORTING} />
          <MenuItem
            label="Events"
            icon={<Icon.AnimationCalendar />}
            path={ROUTE.EVENTS}
            entries={EVENTS}
            activePath={ROUTE.EVENTS}
          />
        </Section>
      </Nav>

      <Bottom>
        <Sandbox>
          <Icon.Sandbox />
          Sandbox is {isSandbox ? 'on' : 'off'}
          <StyledToggle
            isChecked={isSandbox}
            onCheckedChange={handleSandboxChange}
            isDisabled={!currentPlatform?.isLiveEnabled}
          />
        </Sandbox>
      </Bottom>
    </Wrapper>
  );
});
